import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid2 as Grid,
  IconButton,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import { NetsapiensCsp } from 'apis/agency/telephony/types';
import { useEffect, useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import { MdOutlineEdit } from 'react-icons/md';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { confirmDialog } from 'utils/helperFunctions';
import NetsapiensCspDrawer from './NetsapiensCspDrawer';
import { formatOutboundSipUris, maskSecret, normalizeCsps } from '../utils/netsapiensCsp';

interface Props {
  initialCsps: NetsapiensCsp[];
  onPersistCsps: (csps: NetsapiensCsp[]) => Promise<void>;
  title: string;
  description: string;
}

const DETAIL_FIELDS: { key: keyof NetsapiensCsp; label: string; mask?: boolean }[] = [
  { key: 'csp_name', label: 'CSP Name' },
  { key: 'api_url', label: 'API URL' },
  { key: 'portal_url', label: 'Portal URL' },
  { key: 'client_id', label: 'Client ID' },
  { key: 'client_secret', label: 'Client Secret', mask: true },
  { key: 'outbound_sip_uri', label: 'Outbound SIP URI' }
];

export default function NetsapiensCspSection({ initialCsps, onPersistCsps, title, description }: Props) {
  const theme = useTheme();
  const [csps, setCsps] = useState<NetsapiensCsp[]>(() => normalizeCsps(initialCsps));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setCsps(normalizeCsps(initialCsps));
  }, [initialCsps]);

  const openAddDrawer = () => {
    setEditingIndex(null);
    setDrawerOpen(true);
  };

  const openEditDrawer = (index: number) => {
    setEditingIndex(index);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingIndex(null);
  };

  const persistCsps = async (nextCsps: NetsapiensCsp[]) => {
    const previousCsps = csps;
    setIsSaving(true);
    setCsps(nextCsps);

    try {
      await onPersistCsps(nextCsps);
      closeDrawer();
    } catch {
      setCsps(previousCsps);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (index: number, cspName: string) => {
    const result = await confirmDialog({
      title: 'Delete CSP?',
      text: `Remove "${cspName}" from Netsapiens configuration?`,
      icon: 'warning',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      await persistCsps(csps.filter((_, itemIndex) => itemIndex !== index));
    }
  };

  const handleSave = async (values: NetsapiensCsp) => {
    if (editingIndex === null) {
      await persistCsps([...csps, values]);
      return;
    }

    const nextCsps = [...csps];
    nextCsps[editingIndex] = {
      ...values,
      code: csps[editingIndex]?.code,
      state: csps[editingIndex]?.state
    };
    await persistCsps(nextCsps);
  };

  const editingCsp = editingIndex === null ? null : csps[editingIndex] || null;
  const existingCspNames = csps
    .map((csp, index) => (editingIndex === index ? null : csp.csp_name))
    .filter(Boolean) as string[];

  return (
    <>
      <Box
        bgcolor={theme.palette.background.paper}
        padding={{ xs: 2, lg: 3 }}
        borderRadius='4px'
        border='1px solid #ECECEC'
      >
        <Stack direction='row' justifyContent='space-between' alignItems='flex-start' gap={2} mb={2}>
          <Box>
            <Typography fontWeight={500} fontSize={16}>
              {title}
            </Typography>
            <Typography color='secondary.main' fontSize={14}>
              {description}
            </Typography>
          </Box>
          <Button
            variant='contained'
            onClick={openAddDrawer}
            sx={{ borderRadius: '18px', whiteSpace: 'nowrap' }}
            disabled={isSaving}
          >
            + Add CSP
          </Button>
        </Stack>

        {csps.length === 0 ? (
          <Box py={4} textAlign='center' border='1px dashed #ECECEC' borderRadius='8px'>
            <Typography color='secondary.main' fontSize={14}>
              No CSPs configured yet. Add one to enable Netsapiens login.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {csps.map((csp, index) => (
              <Accordion
                key={`${csp.csp_name}-${index}`}
                disableGutters
                elevation={0}
                sx={{
                  border: '1px solid #ECECEC',
                  borderRadius: '8px !important',
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary
                  expandIcon={<FaChevronDown size={14} />}
                  sx={{
                    px: 2,
                    '& .MuiAccordionSummary-content': { alignItems: 'center', justifyContent: 'space-between', my: 1 }
                  }}
                >
                  <Typography fontWeight={500}>{csp.csp_name || `CSP ${index + 1}`}</Typography>
                  <Stack direction='row' spacing={0.5} onClick={(event) => event.stopPropagation()}>
                    <IconButton size='small' onClick={() => openEditDrawer(index)} disabled={isSaving}>
                      <MdOutlineEdit size={18} />
                    </IconButton>
                    <IconButton
                      size='small'
                      onClick={() => handleDelete(index, csp.csp_name)}
                      sx={{ color: '#DA0808' }}
                      disabled={isSaving}
                    >
                      <RiDeleteBin6Line size={18} />
                    </IconButton>
                  </Stack>
                </AccordionSummary>

                <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                  <Grid container spacing={2}>
                    {DETAIL_FIELDS.map(({ key, label, mask }) => (
                      <Grid key={key} size={{ xs: 12, md: 6 }}>
                        <Typography fontSize={12} color='secondary.main' mb={0.5}>
                          {label}
                        </Typography>
                        <Typography fontSize={14} sx={{ wordBreak: 'break-all' }}>
                          {mask
                            ? maskSecret(csp[key] as string)
                            : key === 'outbound_sip_uri'
                              ? formatOutboundSipUris(csp.outbound_sip_uri) || '—'
                              : (csp[key] as string) || '—'}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        )}
      </Box>

      <NetsapiensCspDrawer
        open={drawerOpen}
        initialValues={editingCsp}
        existingCspNames={existingCspNames}
        isSaving={isSaving}
        onClose={closeDrawer}
        onSave={handleSave}
      />
    </>
  );
}
